import { useEffect, useMemo, useRef, useState } from "react"
import { SiteVideoInline } from "../../../../siteWidgets/videoPlayer/genericInlineVideoPlayer/siteVideoInline"
import { LiveVideoPlayer } from "../../liveVideoPlayer/components/liveVideoPlayer"
import { SiteVideoData } from "../../../../interfaces"
import { isWcmEditor } from "../../../../../config/serverConfig"
import { videoSourceOption } from "../../../topStory/windows/contentWindow/videoLiveSourceSelector"
import { StripLiveAnimationMode } from "../data/stripLiveData"

const VIEWPORT_LEAVE_DELAY_MS = 150
const WAVE_LOOP_VIDEO = "https://ynet-pic1.yit.co.il/Common/images/WaveLoop.webm"

interface StripLivePlayerStripProps {
    videoTitle: string
    videoAutoPlay: boolean
    videoMuteSound: boolean
    videoShowSoundBtn: boolean
    liveIndicationText: string
    videoShowControlPanel: boolean
    liveVideoSource: videoSourceOption
    isMobileWeb?: boolean
    animationMode?: StripLiveAnimationMode
    videoLiveLocation?: string
    animationDurationSeconds?: number
    indicatorTextColor?: string
    titleColor?: string
}

export const StripLivePlayerStrip = (props: StripLivePlayerStripProps) => {

    const { videoTitle, videoMuteSound, liveVideoSource, videoShowSoundBtn, videoShowControlPanel, liveIndicationText, isMobileWeb, videoLiveLocation, animationMode = "none", animationDurationSeconds = 0, indicatorTextColor, titleColor } = props
    const source = liveVideoSource || { src: "", label: "", lowResSrc: "" }
    const liveTextStyle = indicatorTextColor ? { color: indicatorTextColor } : undefined
    const titleStyle = titleColor ? { color: titleColor } : undefined

    const [isFloatingPlayerVisible, setIsFloatingPlayerVisible] = useState(false)
    const [isWaveAnimationVisible, setIsWaveAnimationVisible] = useState(false)

    const stripRef: any = useRef<HTMLDivElement>(null)
    const playerRef: any = useRef<any>(null)
    const floatingPlayerApiRef: any = useRef<any>(null)
    const isInViewportRef: any = useRef(false)
    const isSoundOnRef: any = useRef(false)
    const userPausedRef: any = useRef(false)
    const isProgrammaticPauseRef: any = useRef(false)
    const hasStartedWaveAnimationRef: any = useRef(false)
    const waveAnimationTimeoutRef: any = useRef<number | null>(null)
    const isFloatingPlayerVisibleRef: any = useRef(false)
    const leaveViewportTimeoutRef: any = useRef<number | null>(null)

    const flowPlayerData: SiteVideoData = useMemo(() => {
        return {
            mediaType: "MEDIA_VIDEO",
            url: source.lowResSrc || source.src,
            fullScreenUrl: source.src,
            mediaId: "",
            poster: "",
            youtube_id: "",
            isLive: true,
            isMuteAvailable: true,
            blockAbroadViews: false,
            isSoundButtonVisible: videoShowSoundBtn,
            isPlayerBarVisible: videoShowControlPanel,
            isAutoPlaying: true,
            thisLocation: videoLiveLocation
        }
    }, [source, videoShowControlPanel, videoShowSoundBtn, videoLiveLocation])

    const clearWaveAnimationTimeout = () => {
        if (waveAnimationTimeoutRef.current !== null) {
            window.clearTimeout(waveAnimationTimeoutRef.current)
            waveAnimationTimeoutRef.current = null
        }
    }

    const clearLeaveViewportTimeout = () => {
        if (leaveViewportTimeoutRef.current !== null) {
            window.clearTimeout(leaveViewportTimeoutRef.current)
            leaveViewportTimeoutRef.current = null
        }
    }

    const startWaveAnimationIfNeeded = () => {
        if (hasStartedWaveAnimationRef.current) {
            return
        }

        hasStartedWaveAnimationRef.current = true

        if (animationMode === "none") {
            return
        }

        if (animationMode === "always") {
            setIsWaveAnimationVisible(true)
            return
        }

        if (animationMode === "timed" && animationDurationSeconds > 0) {
            setIsWaveAnimationVisible(true)
            clearWaveAnimationTimeout()
            waveAnimationTimeoutRef.current = window.setTimeout(() => {
                setIsWaveAnimationVisible(false)
                waveAnimationTimeoutRef.current = null
            }, animationDurationSeconds * 1000)
        }
    }

    const playStripPlayer = () => {
        if (!playerRef.current || userPausedRef.current) {
            return
        }

        try {
            // Flowplayer: play() is for first start; resume after pause needs togglePlay(true)
            if (playerRef.current.togglePlay) {
                playerRef.current.togglePlay(true)
            } else if (playerRef.current.play) {
                playerRef.current.play()
            }
        } catch (e) { }
    }

    const pauseStripPlayer = () => {
        if (!playerRef.current) {
            return
        }

        isProgrammaticPauseRef.current = true
        try {
            playerRef.current.pause && playerRef.current.pause()
        } catch (e) { }
        setTimeout(() => {
            isProgrammaticPauseRef.current = false
        }, 0)
    }

    const setStripMuted = (shouldMute: boolean) => {
        if (!playerRef.current) {
            return
        }

        try {
            if (!!playerRef.current.muted === shouldMute) {
                isSoundOnRef.current = !shouldMute
                return
            }

            if (playerRef.current.toggleMute) {
                playerRef.current.toggleMute()
            } else if (playerRef.current.setOpts) {
                playerRef.current.setOpts({ muted: shouldMute })
            }

            isSoundOnRef.current = !shouldMute
        } catch (e) { }
    }

    const openFloatingPlayer = () => {
        if (isFloatingPlayerVisibleRef.current || userPausedRef.current) {
            return
        }

        pauseStripPlayer()
        isFloatingPlayerVisibleRef.current = true
        setIsFloatingPlayerVisible(true)
    }

    const closeFloatingPlayer = (options?: { remuteStrip?: boolean; resumeStrip?: boolean }) => {
        const remuteStrip = options && options.remuteStrip
        const resumeStrip = options && options.resumeStrip

        isFloatingPlayerVisibleRef.current = false
        setIsFloatingPlayerVisible(false)

        // Keep LiveVideoPlayer mounted for slide-out; pause its playback while closed
        try {
            const floatingPlayer = floatingPlayerApiRef.current
            if (floatingPlayer && floatingPlayer.pause) {
                floatingPlayer.pause()
            }
        } catch (e) { }

        if (remuteStrip) {
            setStripMuted(true)
        }

        if (resumeStrip) {
            userPausedRef.current = false
            playStripPlayer()
        }
    }

    const handleLeaveViewport = () => {
        if (isInViewportRef.current) {
            return
        }

        if (isSoundOnRef.current && !userPausedRef.current) {
            openFloatingPlayer()
            return
        }

        pauseStripPlayer()
    }

    const handleEnterViewport = () => {
        startWaveAnimationIfNeeded()

        if (isFloatingPlayerVisibleRef.current) {
            closeFloatingPlayer({ remuteStrip: false, resumeStrip: true })
            setStripMuted(false)
            return
        }

        playStripPlayer()
    }

    const handleFullscreenEnter = () => {
        setStripMuted(false)
    }

    const handleVolumeChange = () => {
        if (!playerRef.current) {
            return
        }

        isSoundOnRef.current = !playerRef.current.muted

        if (!isInViewportRef.current && isSoundOnRef.current && !userPausedRef.current) {
            openFloatingPlayer()
        }
    }

    const handlePlayerPlay = () => {
        userPausedRef.current = false
    }

    const handlePlayerPause = () => {
        if (!isProgrammaticPauseRef.current) {
            userPausedRef.current = true
        }
    }

    const handleMobileVideoClick = (event: any) => {
        if (!isMobileWeb || !playerRef.current) {
            return
        }

        const target = event.target as HTMLElement
        if (!target) {
            return
        }

        const clickedControl = target.closest(".fp-volume-mute-unmute, .fp-fullscreen, .fp-volume, .fp-controls, .mute-btn, .close-btn")
        if (clickedControl) {
            return
        }

        try {
            if (playerRef.current.paused) {
                userPausedRef.current = false
                playStripPlayer()
            } else {
                userPausedRef.current = true
                pauseStripPlayer()
            }
        } catch (e) { }
    }

    const handlePlayerLoaded = (player) => {
        playerRef.current = player
        player.setOpts({ muted: isMobileWeb ? true : videoMuteSound })
        isSoundOnRef.current = !(isMobileWeb ? true : videoMuteSound)

        player.addEventListener("volumechange", handleVolumeChange)
        player.addEventListener("play", handlePlayerPlay)
        player.addEventListener("pause", handlePlayerPause)
        player.on && player.on("fullscreenenter", handleFullscreenEnter)

        if (isInViewportRef.current && !userPausedRef.current) {
            playStripPlayer()
        }
    }

    const onIsFloatingPlayerVisibleChange = (isOpen: boolean) => {
        if (isOpen) {
            openFloatingPlayer()
            return
        }

        // X close: fully dismiss floating player; user must unmute again in the strip
        closeFloatingPlayer({ remuteStrip: true, resumeStrip: false })
    }

    useEffect(() => {
        if (isWcmEditor()) {
            return
        }

        const stripElement = stripRef.current
        if (!stripElement || typeof IntersectionObserver === "undefined") {
            return
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const isIntersecting = entry.isIntersecting
                    isInViewportRef.current = isIntersecting

                    if (isIntersecting) {
                        clearLeaveViewportTimeout()
                        handleEnterViewport()
                    } else {
                        clearLeaveViewportTimeout()
                        leaveViewportTimeoutRef.current = window.setTimeout(() => {
                            leaveViewportTimeoutRef.current = null
                            handleLeaveViewport()
                        }, VIEWPORT_LEAVE_DELAY_MS)
                    }
                })
            },
            { threshold: 0.25 }
        )

        observer.observe(stripElement)

        return () => {
            observer.disconnect()
            clearWaveAnimationTimeout()
            clearLeaveViewportTimeout()

            if (playerRef.current) {
                playerRef.current.removeEventListener("volumechange", handleVolumeChange)
                playerRef.current.removeEventListener("play", handlePlayerPlay)
                playerRef.current.removeEventListener("pause", handlePlayerPause)
                playerRef.current.off && playerRef.current.off("fullscreenenter", handleFullscreenEnter)
            }
        }
    }, [animationMode, animationDurationSeconds, isMobileWeb])

    // Force muted autoplay on the floating player once its Flowplayer API is ready
    useEffect(() => {
        if (!isFloatingPlayerVisible) {
            return
        }

        let tries = 0
        const intervalId = window.setInterval(() => {
            tries++
            const player = floatingPlayerApiRef.current
            if (!player) {
                if (tries > 50) {
                    window.clearInterval(intervalId)
                }
                return
            }

            window.clearInterval(intervalId)

            try {
                if (player.setOpts) {
                    player.setOpts({ muted: true })
                }
                if (player.togglePlay) {
                    player.togglePlay(true)
                } else if (player.play) {
                    player.play()
                }
            } catch (e) { }
        }, 100)

        return () => window.clearInterval(intervalId)
    }, [isFloatingPlayerVisible])

    return (
        <div className="strip-live-player-strip" ref={stripRef}>
            {isWcmEditor() ?
                <video className="editor-video" src={source.lowResSrc || source.src}></video>
                :
                <div className="strip-live-video-wrapper" onClick={handleMobileVideoClick}>
                    <SiteVideoInline
                        hideVideoAds
                        data={flowPlayerData}
                        isShareButton={false}
                        applyStickyness={false}
                        onPlayerLoaded={handlePlayerLoaded}
                    />
                </div>
            }

            <div className="live-details">
                {isWaveAnimationVisible ?
                    <video className="live-icon" src={WAVE_LOOP_VIDEO} autoPlay loop muted playsInline />
                    :
                    <div className="live-icon live-icon-svg"></div>
                }

                <div className="live-text" style={liveTextStyle}>{liveIndicationText}</div>

                <div className="program-title" style={titleStyle} dangerouslySetInnerHTML={{ __html: videoTitle || "" }} />
            </div>

            {!isWcmEditor() &&
                <LiveVideoPlayer
                    videoAutoPlay={true}
                    videoMuteSound={true}
                    thisLocation={"strip"}
                    videoTitle={videoTitle}
                    liveVideoSource={source}
                    isMobileWeb={isMobileWeb}
                    isMinimizedVisible={false}
                    videoShowSoundBtn={videoShowSoundBtn}
                    isMinimizedDefaultView={!!isMobileWeb}
                    liveIndicationText={liveIndicationText}
                    playerRefForExternal={floatingPlayerApiRef}
                    videoShowControlPanel={videoShowControlPanel}
                    externalIsOpenTrigger={isFloatingPlayerVisible}
                    onExternalIsOpenTriggerChange={onIsFloatingPlayerVisibleChange}
                />
            }
        </div>
    )
}

StripLivePlayerStrip.siteScriptName = "StripLivePlayerStrip"
